/**
 * Profit by customer — rolls the (customer × product) baseline rows up to one row
 * per billing-customer group, summing seats and MRR across all their products.
 * Expanding a row reveals the per-product breakdown for that customer.
 */

import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import type { ComputedProfitRow } from '../../utils/profitCompute';
import { fmtMoney, fmtPct } from '../../utils/profitCompute';
import { headCellSx, bodyCellSx, ProfitFlagChips } from './profitFlags';

interface CustomerGroup {
  billingCustomerId: string;
  billingCustomerName: string | null;
  products: number;
  seats: number;
  mrrBuy: number;
  mrrSell: number;
  mrrProfit: number;
  knownSell: number;
  knownProfit: number;
  rows: ComputedProfitRow[];
}

function groupByCustomer(rows: ComputedProfitRow[]): CustomerGroup[] {
  const map = new Map<string, CustomerGroup>();
  for (const r of rows) {
    let g = map.get(r.billingCustomerId);
    if (!g) {
      g = {
        billingCustomerId: r.billingCustomerId,
        billingCustomerName: r.billingCustomerName,
        products: 0,
        seats: 0,
        mrrBuy: 0,
        mrrSell: 0,
        mrrProfit: 0,
        knownSell: 0,
        knownProfit: 0,
        rows: [],
      };
      map.set(r.billingCustomerId, g);
    }
    g.products += 1;
    g.seats += r.seats;
    g.rows.push(r);
    if (r.mrrBuy !== null && r.mrrSell !== null && r.mrrProfit !== null) {
      g.mrrBuy += r.mrrBuy;
      g.mrrSell += r.mrrSell;
      g.mrrProfit += r.mrrProfit;
      g.knownSell += r.mrrSell;
      g.knownProfit += r.mrrProfit;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.mrrProfit - a.mrrProfit);
}

const CustomerRow: React.FC<{ group: CustomerGroup; zebra: boolean }> = ({ group, zebra }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow sx={{ backgroundColor: zebra ? 'action.hover' : 'transparent' }}>
        <TableCell sx={{ ...bodyCellSx, width: 36 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)} aria-label="expand">
            {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={bodyCellSx}>{group.billingCustomerName ?? '—'}</TableCell>
        <TableCell sx={bodyCellSx} align="right">{group.products}</TableCell>
        <TableCell sx={bodyCellSx} align="right">{group.seats}</TableCell>
        <TableCell sx={bodyCellSx} align="right">{fmtMoney(group.mrrBuy)}</TableCell>
        <TableCell sx={bodyCellSx} align="right">{fmtMoney(group.mrrSell)}</TableCell>
        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{fmtMoney(group.mrrProfit)}</TableCell>
        <TableCell sx={bodyCellSx} align="right">
          {fmtPct(group.knownSell ? group.knownProfit / group.knownSell : null)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={8}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Product</TableCell>
                    <TableCell sx={headCellSx} align="right">Seats</TableCell>
                    <TableCell sx={headCellSx} align="right">MRR Buy</TableCell>
                    <TableCell sx={headCellSx} align="right">MRR Sell</TableCell>
                    <TableCell sx={headCellSx} align="right">MRR Profit</TableCell>
                    <TableCell sx={headCellSx} align="right">Margin</TableCell>
                    <TableCell sx={headCellSx}>Flags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.rows.map(r => (
                    <TableRow key={r.vendorProductId}>
                      <TableCell sx={bodyCellSx}>{r.productName ?? r.sku ?? '—'}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{r.seats || r.billedSeats}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{fmtMoney(r.mrrBuy)}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{fmtMoney(r.mrrSell)}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{fmtMoney(r.mrrProfit)}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">{fmtPct(r.marginPct)}</TableCell>
                      <TableCell sx={bodyCellSx}><ProfitFlagChips flags={r.flags} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const ProfitByCustomerTable: React.FC<{ rows: ComputedProfitRow[] }> = ({ rows }) => {
  const groups = useMemo(() => groupByCustomer(rows), [rows]);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx} />
            <TableCell sx={headCellSx}>Customer</TableCell>
            <TableCell sx={headCellSx} align="right">Products</TableCell>
            <TableCell sx={headCellSx} align="right">Seats</TableCell>
            <TableCell sx={headCellSx} align="right">MRR Buy</TableCell>
            <TableCell sx={headCellSx} align="right">MRR Sell</TableCell>
            <TableCell sx={headCellSx} align="right">MRR Profit</TableCell>
            <TableCell sx={headCellSx} align="right">Margin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g, i) => (
            <CustomerRow key={g.billingCustomerId} group={g} zebra={i % 2 === 1} />
          ))}
          {!groups.length && (
            <TableRow>
              <TableCell sx={bodyCellSx} colSpan={8} align="center">No data.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
